// ============================================================
// VantaFog.js
//
// Background layer — renders one of two strategies:
//   DESKTOP  → Vanta.js animated fog (WebGL, Three.js)
//   MOBILE   → Looping MP4 video (h265 with h264 fallback)
//
// Props:
//   active {bool} — passed from App.js scroll handler.
//                   When false (user scrolled past hero),
//                   Vanta is destroyed to free GPU memory.
//                   Video just stays mounted (cheap, GPU-decoded).
//
// To change fog colors → edit the `darkConfig` / `lightConfig`
// objects below. All other visual knobs live there too.
// ============================================================

import React, { useEffect, useRef, useMemo } from 'react';
import * as THREE from 'three';
import FOG from 'vanta/dist/vanta.fog.min';
import { useTheme } from '@material-ui/core/styles';

// ── Breakpoint: screens narrower than this get video instead of Vanta ──
const MOBILE_BREAKPOINT = 900; // px — matches MUI's "md" breakpoint

// ── Vanta fog configs ───────────────────────────────────────────────────
// Tweak these freely — they only affect the animated fog, not the video.
const darkConfig = {
  highlightColor: 0x6366f1, // indigo highlight
  midtoneColor:   0x22d3ee, // cyan midtone
  lowlightColor:  0x1e1b4b, // deep navy shadow
  baseColor:      0x0a0a0a, // near-black base
  blurFactor:     0.6,
  speed:          0.75,
  zoom:           0.85,
};

const lightConfig = {
  highlightColor: 0x6366f1, // same indigo — keeps brand consistent
  midtoneColor:   0x22d3ee,
  lowlightColor:  0x94a3b8, // slate shadow
  baseColor:      0xf1f5f9, // off-white base
  blurFactor:     0.5,
  speed:          0.65,
  zoom:           0.9,
};

// ── Video source paths ──────────────────────────────────────────────────
// Files live in /public/media/videos/ — update filenames here if renamed.
// h265 is served first (smaller file, Safari/modern Chrome); h264 is the
// universal fallback.
const VIDEO = {
  dark: {
    h265:   '/media/videos/dark_h265.mp4',
    h264:   '/media/videos/dark_h264.mp4',
    poster: '/media/videos/dark_poster.jpg',
  },
  light: {
    h265:   '/media/videos/light_h265.mp4',
    h264:   '/media/videos/light_h264.mp4',
    poster: '/media/videos/light_poster.jpg',
  },
};

// ── Overlay opacity ─────────────────────────────────────────────────────
// Darkens/lightens the background so text stays readable.
// Increase if your hero text becomes hard to read.
const OVERLAY = {
  dark:  'rgba(0,0,0,0.45)',
  light: 'rgba(255,255,255,0.52)',
};

// ============================================================
// Component
// ============================================================
const VantaFog = ({ active }) => {
  const vantaRef    = useRef(null); // DOM node Vanta attaches to
  const vantaEffect = useRef(null); // Vanta instance (for cleanup)
  const theme       = useTheme();
  const isDark      = theme.palette.type === 'dark';

  // Compute once on mount — window size won't change without a reload
  // and UA string is static. useMemo avoids re-checking every render.
  const isMobile = useMemo(() => (
    window.innerWidth < MOBILE_BREAKPOINT ||
    /Android|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent)
  ), []);

  // ── Desktop: Vanta lifecycle ──────────────────────────────────────────
  // Runs when: theme changes OR active prop changes.
  // `active` = false means user has scrolled away → destroy Vanta to
  // free GPU resources. It'll re-init when they scroll back up.
  useEffect(() => {
    // Skip entirely on mobile — video handles the background there
    if (isMobile) return;

    if (active) {
      // Destroy old instance before creating a new one (theme switch)
      if (vantaEffect.current) vantaEffect.current.destroy();

      vantaEffect.current = FOG({
        el:            vantaRef.current,
        THREE:         THREE,
        mouseControls: true,
        touchControls: true,
        gyroControls:  false,
        minHeight:     200,
        minWidth:      200,
        scale:         1.0,
        scaleMobile:   1.0,
        ...(isDark ? darkConfig : lightConfig),
      });
    } else {
      // User scrolled past hero — tear down WebGL context
      if (vantaEffect.current) {
        vantaEffect.current.destroy();
        vantaEffect.current = null;
      }
    }

    return () => {
      if (vantaEffect.current) {
        vantaEffect.current.destroy();
        vantaEffect.current = null;
      }
    };
  }, [isDark, isMobile, active]);

  // ── Shared container style ────────────────────────────────────────────
  // contain:strict tells the browser this element won't affect outside
  // layout — eliminates the Vanta/video div as a CLS contributor.
  const fixedFill = {
    position: 'fixed',
    top:      0,
    left:     0,
    width:    '100vw',
    height:   '100vh',
    zIndex:   -1,
    contain:  'strict',   // CLS: isolates this from layout calculations
  };

  const canPlayHEVC = (() => { //Detect HEVC support properly
  const v = document.createElement("video");
  return (
    v.canPlayType('video/mp4; codecs="hvc1"') === "probably" ||
    v.canPlayType('video/mp4; codecs="hvc1"') === "maybe"
  );
  })();

  return (
    <>
      {/* ── DESKTOP: Vanta fog canvas ───────────────────────────────── */}
      {!isMobile && (
        <div ref={vantaRef} style={fixedFill} />
      )}

      {/* ── MOBILE: Looping video ───────────────────────────────────── */}
      {/* `key` forces React to remount the <video> when theme changes,
          which triggers a fresh src load instead of a flicker on the
          old video. The poster shows instantly during that load. */}
      {isMobile && (
        <div
          style={{
            ...fixedFill,
            overflow:   'hidden',
            background: isDark ? '#0a0a0a' : '#f1f5f9', // solid bg while video loads
          }}
        >
          <video
            key={isDark ? 'dark' : 'light'}
            autoPlay
            loop
            muted
            playsInline
            // `metadata` instead of `auto` — fetches duration + first frame only.
            // Saves significant bandwidth on cellular. The video will still autoplay
            // because the browser buffers ahead once playback starts.
            preload="metadata"
            poster={isDark ? VIDEO.dark.poster : VIDEO.light.poster}
            style={{
              position:          'absolute',
              top:               0,
              left:              0,
              width:             '100vw',
              height:            '100vh',
              objectFit:         'cover',
              // GPU compositing hints — reduces paint cost on mobile
              transform:         'translateZ(0)',
              backfaceVisibility: 'hidden',
              willChange:        'transform',
            }}
          >
            {/* h265 first — ~40% smaller than h264, supported on iOS Safari & modern Chrome */}
            {canPlayHEVC ? (
              <source src={isDark ? VIDEO.dark.h265  : VIDEO.light.h265} type='video/mp4; codecs="hvc1"' />
            ) : (
              <source src={isDark ? VIDEO.dark.h264  : VIDEO.light.h264} type='video/mp4; codecs="avc1"' />
            )}
          </video>
        </div>
      )}

      {/* ── OVERLAY: Applies to both desktop + mobile ──────────────── */}
      {/* Tints the background to improve text contrast.
          pointerEvents none = clicks pass through to content below. */}
      <div
        style={{
          position:      'fixed',
          inset:         0,
          background:    isDark ? OVERLAY.dark : OVERLAY.light,
          pointerEvents: 'none',
          width:             '100vw',
          height:            '100vh',
          zIndex:        -1,
        }}
      />
    </>
  );
};

export default VantaFog;